import {
  CreateUserDto,
  OrgRole,
  UpdateUserDto,
  User,
  UserType,
} from "@autfit/shared-types";
import { organizationsAPI, usersAPI } from "../services/api";

/**
 * School admin = ORG_USER with orgRole ADMINISTRATIVE and a school. Same rule
 * as the backend (`users/user-management-rules.ts`); the legacy `role` column
 * is not used. See documentation/access-control.md "User management".
 */
export const isSchoolAdmin = (user: User | null | undefined): boolean =>
  !!user &&
  user.type === UserType.ORG_USER &&
  user.orgRole === OrgRole.ADMINISTRATIVE &&
  !!user.organizationId;

export interface UserEditApi {
  update: (userId: string, data: UpdateUserDto) => Promise<User>;
  remove: (userId: string) => Promise<void>;
}

/**
 * Super admins edit through `/organizations/:orgId/users/:id`
 * (SuperAdminGuard), school admins through `/users/:id`.
 */
export const userEditApi = (
  currentUser: User | null | undefined,
  organizationId: string,
): UserEditApi =>
  currentUser?.type === UserType.SUPER_ADMIN
    ? {
        update: (userId, data) =>
          organizationsAPI.updateUser(organizationId, userId, data),
        remove: (userId) => organizationsAPI.deleteUser(organizationId, userId),
      }
    : { update: usersAPI.update, remove: usersAPI.delete };

/**
 * Super admins create through `POST /organizations/:orgId/users`
 * (SuperAdminGuard, school from the URL, no `organizationId` in the body);
 * school admins through `POST /users` (own school only).
 */
export const createUserAs = (
  currentUser: User | null | undefined,
  data: CreateUserDto,
): Promise<User> => {
  if (currentUser?.type !== UserType.SUPER_ADMIN) return usersAPI.create(data);
  const {
    organizationId,
    email,
    phoneNumber,
    firstName,
    lastName,
    password,
    orgRole,
  } = data;
  return organizationsAPI.createUser(organizationId ?? "", {
    email,
    phoneNumber,
    firstName: firstName ?? "",
    lastName: lastName ?? "",
    password,
    orgRole,
  });
};
